// Repo-hosted blog posts.
//
// Every `.md` file in this folder is a published article that ships inside the
// bundle instead of being fetched from the API. Because the content is known at
// build time, `scripts/prerender-blog.mjs` can emit a real static HTML page per
// post (with the article text and per-post meta baked into the markup), which is
// what search engines and social crawlers actually index.
//
// The backend blog CMS still works: anything the API returns that does not have
// a matching slug here is rendered the old way. See `src/pages/Blog`.
//
// NOTE: the frontmatter format is intentionally simple `key: value` lines so the
// Node prerender script can parse it without a YAML dependency. If you change
// the shape here, mirror it in `scripts/prerender-blog.mjs`.

import { marked } from 'marked';

export interface RepoPost {
    slug: string;
    title: string;
    description: string;
    excerpt: string;
    /** ISO date (YYYY-MM-DD) used for sorting, display and `datePublished`. */
    date: string;
    author: string;
    coverImageUrl: string | null;
    tags: string[];
    /** Article body compiled from Markdown to HTML. */
    html: string;
    readingMinutes: number;
}

type Frontmatter = Record<string, string>;

/** Split `---` frontmatter from the Markdown body. */
function splitFrontmatter(raw: string): { data: Frontmatter; body: string } {
    const normalised = raw.replace(/\r\n/g, '\n');
    const match = /^---\n([\s\S]*?)\n---\n?/.exec(normalised);
    if (!match) return { data: {}, body: normalised };

    const data: Frontmatter = {};
    for (const line of (match[1] ?? '').split('\n')) {
        if (!line.trim()) continue;
        const idx = line.indexOf(':');
        if (idx === -1) continue;
        const key = line.slice(0, idx).trim();
        const value = line.slice(idx + 1).trim();
        if (key) data[key] = value;
    }
    return { data, body: normalised.slice(match[0].length) };
}

/** Roughly 200 words per minute, floored at 1. */
function readingTime(body: string): number {
    const words = body.trim().split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(words / 200));
}

const rawPosts = import.meta.glob('./*.md', {
    query: '?raw',
    import: 'default',
    eager: true,
}) as Record<string, string>;

function buildPosts(): RepoPost[] {
    const posts: RepoPost[] = [];

    for (const [path, raw] of Object.entries(rawPosts)) {
        const fileSlug = path.replace(/^\.\//, '').replace(/\.md$/, '');
        const { data, body } = splitFrontmatter(raw);
        if (!data.title) continue;

        posts.push({
            slug: data.slug || fileSlug,
            title: data.title,
            description: data.description || data.excerpt || '',
            excerpt: data.excerpt || data.description || '',
            date: data.date || '',
            author: data.author || 'YourChecka',
            coverImageUrl: data.cover || null,
            tags: data.tags
                ? data.tags
                      .split(',')
                      .map((t) => t.trim())
                      .filter(Boolean)
                : [],
            html: marked.parse(body, { async: false }) as string,
            readingMinutes: readingTime(body),
        });
    }

    // Newest first.
    return posts.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

export const repoPosts: RepoPost[] = buildPosts();

export const repoPostSlugs: ReadonlySet<string> = new Set(repoPosts.map((p) => p.slug));

export function getRepoPost(slug: string | undefined): RepoPost | undefined {
    if (!slug) return undefined;
    return repoPosts.find((p) => p.slug === slug);
}
